<script setup>
// «Клиенты и источники» для отчёта «Каналы» (Р21: перенесено из отчёта
// ИИ-агенты — весь клиентский срез теперь в одном месте). Самодостаточна:
// тянет AnalyticsAPI.overview + contacts за 30 дней. Карточки (клиенты,
// трафик сообщений, рассылки), график регистраций, таблица последних клиентов
// и выгрузка контактов в Excel.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import format from 'date-fns/format';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BarRow from 'dashboard/components-next/analytics/BarRow.vue';
import AnalyticsAPI from 'dashboard/api/analytics';

const { t } = useI18n();

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const PERIOD_DAYS = 30;

const isFetching = ref(false);
const isExporting = ref(false);
const overview = ref(null);
const clients = ref([]);

const formatNumber = value =>
  value === null || value === undefined ? '—' : Number(value).toLocaleString();

const formatTs = value =>
  value ? format(new Date(value * 1000), 'dd.MM.yyyy HH:mm') : '—';

const clientCards = computed(() => {
  if (!overview.value) return [];
  const o = overview.value;
  const m = o.messages || {};
  return [
    {
      key: 'clients_total',
      label: t('ANALYTICS.CLIENTS.TOTAL'),
      value: formatNumber(o.clients_total),
    },
    {
      key: 'messages_in',
      label: t('ANALYTICS.CLIENTS.MESSAGES_IN'),
      value: formatNumber(m.incoming),
    },
    {
      key: 'messages_out',
      label: t('ANALYTICS.CLIENTS.MESSAGES_OUT'),
      value: formatNumber(m.outgoing),
    },
    {
      key: 'broadcasts',
      label: t('ANALYTICS.CLIENTS.BROADCASTS'),
      value: formatNumber(o.broadcasts_sent),
    },
  ];
});

const registrationRows = computed(() => {
  const rows = overview.value?.registrations || [];
  const max = rows.reduce((acc, r) => Math.max(acc, r.count), 0);
  return rows.map(r => ({ ...r, max }));
});

const fetchData = async () => {
  isFetching.value = true;
  try {
    const until = new Date();
    const since = new Date(until.getTime() - PERIOD_DAYS * DAY_IN_MS);
    const params = { since: since.toISOString(), until: until.toISOString() };
    const [overviewResponse, contactsResponse] = await Promise.all([
      AnalyticsAPI.overview(params),
      AnalyticsAPI.contacts(params),
    ]);
    overview.value = overviewResponse.data;
    clients.value = contactsResponse.data.payload || [];
  } catch {
    overview.value = null;
    clients.value = [];
  } finally {
    isFetching.value = false;
  }
};

const handleExport = async () => {
  isExporting.value = true;
  try {
    const response = await AnalyticsAPI.exportContacts();
    const fileName = `contacts-${format(new Date(), 'dd-MM-yyyy')}.csv`;
    downloadCsvFile(fileName, response.data);
  } catch {
    useAlert(t('ANALYTICS.EXPORT.ERROR_MESSAGE'));
  } finally {
    isExporting.value = false;
  }
};

onMounted(fetchData);
</script>

<template>
  <section class="flex flex-col gap-4 pt-6 mt-6 border-t border-n-weak">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('ANALYTICS.CLIENTS.TITLE') }}
      </h3>
      <Button
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-download"
        :label="t('ANALYTICS.EXPORT.BUTTON')"
        :is-loading="isExporting"
        :disabled="isExporting"
        @click="handleExport"
      />
    </div>

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-6 text-n-slate-11"
    >
      <Spinner />
    </div>

    <template v-else>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div
          v-for="card in clientCards"
          :key="card.key"
          class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <span class="text-xs text-n-slate-11">{{ card.label }}</span>
          <span class="text-lg font-medium text-n-slate-12">
            {{ card.value }}
          </span>
        </div>
      </div>

      <div v-if="registrationRows.length" class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-11">
          {{ t('ANALYTICS.CLIENTS.REGISTRATIONS') }}
        </h4>
        <BarRow
          v-for="row in registrationRows"
          :key="row.date"
          :label="row.date.slice(5)"
          :value="row.count"
          :max="row.max"
        />
      </div>

      <div v-if="clients.length" class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-11">
          {{ t('ANALYTICS.CLIENTS.TABLE_TITLE') }}
        </h4>
        <div class="overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="border-b border-n-weak text-n-slate-11">
                <th class="px-3 py-2 font-medium text-start">
                  {{ t('ANALYTICS.CLIENTS.COL_NAME') }}
                </th>
                <th class="px-3 py-2 font-medium text-start">
                  {{ t('ANALYTICS.CLIENTS.COL_PHONE') }}
                </th>
                <th class="px-3 py-2 font-medium text-start">
                  {{ t('ANALYTICS.CLIENTS.COL_SYSTEMS') }}
                </th>
                <th class="px-3 py-2 font-medium text-start">
                  {{ t('ANALYTICS.CLIENTS.COL_REGISTERED') }}
                </th>
                <th class="px-3 py-2 font-medium text-start">
                  {{ t('ANALYTICS.CLIENTS.COL_LAST_ACTIVITY') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="client in clients"
                :key="client.id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="px-3 py-2 text-n-slate-12">{{ client.name }}</td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{ client.phone_number || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{ client.systems.join(', ') || '—' }}
                </td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{ formatTs(client.created_at) }}
                </td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{ formatTs(client.last_activity_at) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </section>
</template>
